import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Table,
  ArrowUp,
  ArrowDown,
  ChevronsUpDown,
  Calendar,
  Users,
  FileText,
  ArrowLeft,
  Search,
  Download,
  RefreshCw,
  FileSpreadsheet,
  ChevronLeft,
  ChevronRight
} from "lucide-react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

import { formatDMY } from "@/lib/dmy";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SalesRawDataViewPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [data, setData] = useState([]);
  const [upload, setUpload] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState('asc');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(100);
  const [pageLoading, setPageLoading] = useState(false);

  // Currency keywords for narration
  const CURRENCY_KEYWORDS = [
    'usd', 'dollar', '$', '€', 'euro', 'eur', '£', 'gbp', 'pound', 'sterling',
    '¥', 'yen', 'jpy', 'cny', 'yuan', 'rmb', 'renminbi',
    'aud', 'cad', 'chf', 'franc', 'nzd', 'sgd', 'hkd', 'krw', 'won',
    'myr', 'ringgit', 'thb', 'baht', 'php', 'peso', 'idr', 'rupiah',
    'vnd', 'dong', 'brl', 'real', 'mxn', 'zar', 'rand',
    'aed', 'dirham', 'sar', 'riyal', 'qar', 'kwd', 'dinar', 'bhd', 'omr',
    'btc', 'bitcoin', 'usdt', 'tether', 'ethereum', 'eth', 'tron', 'trx',
    'bnb', 'binance', 'xrp', 'ripple', 'sol', 'solana', 'ada', 'cardano',
    'doge', 'dogecoin', 'ltc', 'litecoin', 'usdc', 'busd', 'crypto',
    'crocin'
  ];

  // Extract narration from row - checks raw_data for currency keywords and values
  const extractNarration = (row) => {
    // Use raw_data if available, otherwise use all row fields
    const rawData = row.raw_data || row;
    const allData = { ...row, ...rawData };
    
    // Combine all string fields to search for currency keywords
    const searchText = Object.values(allData)
      .filter(v => typeof v === 'string')
      .join(' ')
      .toLowerCase();
    
    // Find matching currency keywords
    const foundCurrencies = CURRENCY_KEYWORDS.filter(keyword => 
      searchText.includes(keyword.toLowerCase())
    );
    
    if (foundCurrencies.length === 0) {
      return { hasNarration: false };
    }
    
    // Build narration parts
    const narrationParts = [];
    let hasCrocin = false;
    
    // Check for special "crocin" keyword
    if (searchText.includes('crocin')) {
      hasCrocin = true;
    }
    
    // Look for $ values in shipping and total cost columns
    const keys = Object.keys(allData);
    
    // Find shipping value
    const shippingKey = keys.find(k => k.toLowerCase().includes('shipping') || k.toLowerCase().includes('ship'));
    if (shippingKey && allData[shippingKey]) {
      const shippingVal = String(allData[shippingKey]).trim();
      if (shippingVal && shippingVal !== '0') {
        const numVal = shippingVal.replace(/[^0-9.]/g, '');
        if (numVal) narrationParts.push(`$${numVal} shipping`);
      }
    }
    
    // Find total cost value
    const totalCostKey = keys.find(k => 
      (k.toLowerCase().includes('total') && k.toLowerCase().includes('cost')) ||
      k.toLowerCase() === 'total cost'
    );
    if (totalCostKey && allData[totalCostKey]) {
      const costVal = String(allData[totalCostKey]).trim();
      if (costVal && costVal !== '0') {
        const numVal = costVal.replace(/[^0-9.]/g, '');
        if (numVal) narrationParts.push(`$${numVal} total cost`);
      }
    }
    
    // Get INR/Payment value for display
    let inrAmount = 0;
    const inrKey = keys.find(k => k.toLowerCase() === 'inr' || k.toLowerCase().includes('inr'));
    const paymentKey = keys.find(k => k.toLowerCase().includes('payment') || k.toLowerCase().includes('pay'));
    
    if (inrKey && allData[inrKey]) {
      inrAmount = parseFloat(String(allData[inrKey]).replace(/[^0-9.]/g, '')) || 0;
    } else if (paymentKey && allData[paymentKey]) {
      inrAmount = parseFloat(String(allData[paymentKey]).replace(/[^0-9.]/g, '')) || 0;
    } else {
      inrAmount = row.sell_price || row.amount || 0;
    }
    
    // Determine primary currency indicator
    let currencyFound = foundCurrencies[0].toUpperCase();
    if (currencyFound === 'CROCIN') {
      currencyFound = 'ESR@';
    }
    
    return {
      hasNarration: true,
      currency: hasCrocin ? 'ESR@' : currencyFound,
      inrAmount: inrAmount,
      narrationParts: narrationParts,
      hasCrocin: hasCrocin
    };
  };

  // Fetch data from API with server-side sorting and search
  const fetchPageData = async (page, sort = sortField, order = sortOrder, search = searchTerm) => {
    if (!upload?.upload_date) return;
    
    setPageLoading(true);
    try {
      const skip = (page - 1) * pageSize;
      let url = `${BACKEND_URL}/api/sales/upload-data/${upload.upload_date}?limit=${pageSize}&skip=${skip}`;
      if (upload.uploaded_by && upload.uploaded_by !== 'Unknown') {
        url += `&uploaded_by=${encodeURIComponent(upload.uploaded_by)}`;
      }
      // Add sorting parameters
      if (sort) {
        url += `&sort_field=${encodeURIComponent(sort)}&sort_order=${encodeURIComponent(order)}`;
      }
      // Add search parameter
      if (search && search.trim()) {
        url += `&search=${encodeURIComponent(search.trim())}`;
      }
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const result = await response.json();
        setData(result.sales || []);
        setTotal(result.total || 0);
        setCurrentPage(page);
        setSortField(sort);
        setSortOrder(order);
      }
    } catch (error) {
      console.error("Error fetching page data:", error);
    } finally {
      setPageLoading(false);
    }
  };

  useEffect(() => {
    // Get initial data from localStorage (shared across tabs)
    const stored = localStorage.getItem('salesRawData');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setData(parsed.data || []);
        setUpload(parsed.upload || {});
        setTotal(parsed.total || 0);
      } catch (e) {
        console.error("Failed to parse stored data:", e);
      }
    }
    setLoading(false);
  }, []);

  // Format currency
  const formatCurrency = (value) => {
    if (value === null || value === undefined) return "₹0";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value);
  };

  // Handle sort click - triggers server-side sorting
  const handleSort = (field) => {
    const newOrder = sortField === field && sortOrder === 'asc' ? 'desc' : 'asc';
    fetchPageData(1, field, newOrder, searchTerm);
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    fetchPageData(newPage, sortField, sortOrder, searchTerm);
  };

  // Handle search submit
  const handleSearchSubmit = () => {
    fetchPageData(1, sortField, sortOrder, searchTerm);
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchTerm('');
    fetchPageData(1, sortField, sortOrder, '');
  };

  const totalPages = Math.ceil(total / pageSize);

  // Export to Excel
  const exportToExcel = () => {
    const exportData = data.map((row, idx) => {
      const narration = extractNarration(row);
      return {
        '#': (currentPage - 1) * pageSize + idx + 1,
        'Tracking': row.tracking,
        'Client': row.client_code,
        'Date': row.sale_date,
        'Sell (₹)': row.sell_price,
        'Cost (₹)': row.cost_price || row.cost,
        'Profit (₹)': row.profit,
        'Status': row.status,
        'Uploaded By': row.uploaded_by,
        'Narration': narration.hasNarration ? `${narration.currency} → ₹${narration.inrAmount}` : ''
      };
    });

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sales Data");
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([wbout]), `sales_data_${upload?.upload_date || 'export'}_page${currentPage}.xlsx`);
  };

  // Render sort icon
  const SortIcon = ({ field }) => {
    if (sortField === field) {
      return sortOrder === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />;
    }
    return <ChevronsUpDown className="w-3 h-3 text-slate-400" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  if (data.length === 0 && !upload) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center">
        <FileSpreadsheet className="w-16 h-16 text-slate-300 mb-4" />
        <h2 className="text-xl font-semibold text-slate-600 mb-2">No Data Available</h2>
        <p className="text-slate-500 mb-4">Please open this page from the Sales Upload History panel</p>
        <Button onClick={() => navigate('/sales')} className="gap-2">
          <ArrowLeft className="w-4 h-4" />
          Back to Sales
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="sticky top-0 bg-white border-b shadow-sm z-10">
        <div className="max-w-full mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/sales')} className="gap-2">
                <ArrowLeft className="w-4 h-4" />
                Back
              </Button>
              <div className="border-l pl-4">
                <h1 className="text-lg font-semibold flex items-center gap-2">
                  <Table className="w-5 h-5 text-indigo-600" />
                  Sales Upload Raw Data
                </h1>
                <div className="flex items-center gap-4 text-sm text-slate-500">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {upload?.upload_date || 'Unknown Date'}
                  </span>
                  <span className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    {upload?.uploaded_by || 'Unknown'}
                  </span>
                  <span className="flex items-center gap-1">
                    <FileText className="w-4 h-4" />
                    {total.toLocaleString()} records
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search tracking, client... (Enter)"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearchSubmit()}
                  className="pl-9 w-64"
                />
              </div>
              <Button variant="outline" size="sm" onClick={handleSearchSubmit} disabled={pageLoading}>
                <Search className="w-4 h-4" />
              </Button>
              {searchTerm && (
                <Button variant="ghost" size="sm" onClick={handleClearSearch}>
                  Clear
                </Button>
              )}
              <Button variant="outline" onClick={exportToExcel} className="gap-2">
                <Download className="w-4 h-4" />
                Export Excel
              </Button>
            </div>
          </div>
          {sortField && (
            <div className="mt-2 text-sm text-slate-500">
              Sorted by: <strong>{sortField}</strong> ({sortOrder === 'asc' ? 'A→Z' : 'Z→A'})
            </div>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="max-w-full mx-auto p-4">
        <div className="bg-white rounded-lg shadow overflow-hidden relative">
          {pageLoading && (
            <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-20">
              <RefreshCw className="w-8 h-8 animate-spin text-indigo-500" />
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-slate-100">
                <tr className="border-b">
                  <th className="text-left p-3 font-medium text-slate-600 w-12">#</th>
                  <th 
                    className="text-left p-3 font-medium text-slate-600 cursor-pointer hover:bg-slate-200 select-none"
                    onClick={() => handleSort('tracking')}
                  >
                    <div className="flex items-center gap-1">
                      Tracking <SortIcon field="tracking" />
                    </div>
                  </th>
                  <th 
                    className="text-left p-3 font-medium text-slate-600 cursor-pointer hover:bg-slate-200 select-none"
                    onClick={() => handleSort('client_code')}
                  >
                    <div className="flex items-center gap-1">
                      Client <SortIcon field="client_code" />
                    </div>
                  </th>
                  <th 
                    className="text-left p-3 font-medium text-slate-600 cursor-pointer hover:bg-slate-200 select-none"
                    onClick={() => handleSort('sale_date')}
                  >
                    <div className="flex items-center gap-1">
                      Date <SortIcon field="sale_date" />
                    </div>
                  </th>
                  <th 
                    className="text-right p-3 font-medium text-slate-600 cursor-pointer hover:bg-slate-200 select-none"
                    onClick={() => handleSort('sell_price')}
                  >
                    <div className="flex items-center justify-end gap-1">
                      Sell (₹) <SortIcon field="sell_price" />
                    </div>
                  </th>
                  <th 
                    className="text-right p-3 font-medium text-slate-600 cursor-pointer hover:bg-slate-200 select-none"
                    onClick={() => handleSort('cost_price')}
                  >
                    <div className="flex items-center justify-end gap-1">
                      Cost (₹) <SortIcon field="cost_price" />
                    </div>
                  </th>
                  <th 
                    className="text-right p-3 font-medium text-slate-600 cursor-pointer hover:bg-slate-200 select-none"
                    onClick={() => handleSort('profit')}
                  >
                    <div className="flex items-center justify-end gap-1">
                      Profit (₹) <SortIcon field="profit" />
                    </div>
                  </th>
                  <th 
                    className="text-center p-3 font-medium text-slate-600 cursor-pointer hover:bg-slate-200 select-none"
                    onClick={() => handleSort('status')}
                  >
                    <div className="flex items-center justify-center gap-1">
                      Status <SortIcon field="status" />
                    </div>
                  </th>
                  <th 
                    className="text-left p-3 font-medium text-slate-600 cursor-pointer hover:bg-slate-200 select-none"
                    onClick={() => handleSort('uploaded_by')}
                  >
                    <div className="flex items-center gap-1">
                      Uploaded By <SortIcon field="uploaded_by" />
                    </div>
                  </th>
                  <th className="text-left p-3 font-medium text-slate-600">
                    <div className="flex items-center gap-1">
                      Narration
                      <span className="text-[10px] text-slate-400">(Currency)</span>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.map((row, idx) => {
                  const narration = extractNarration(row);
                  const rowNumber = (currentPage - 1) * pageSize + idx + 1;
                  return (
                    <tr key={row.id || idx} className="border-b hover:bg-slate-50">
                      <td className="p-3 text-slate-400">{rowNumber}</td>
                      <td className="p-3 font-mono text-xs">{row.tracking}</td>
                      <td className="p-3">
                        <Badge variant="outline">{row.client_code || '-'}</Badge>
                      </td>
                      <td className="p-3 text-slate-600">{formatDMY(row.sale_date)}</td>
                      <td className="p-3 text-right text-blue-600">{formatCurrency(row.sell_price)}</td>
                      <td className="p-3 text-right text-orange-600">{formatCurrency(row.cost_price || row.cost)}</td>
                      <td className={`p-3 text-right font-medium ${(row.profit || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {formatCurrency(row.profit)}
                      </td>
                      <td className="p-3 text-center">
                        {row.status === 'matched' ? (
                          <Badge className="bg-green-100 text-green-700">Matched</Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-700">Unmatched</Badge>
                        )}
                      </td>
                      <td className="p-3 text-slate-500">{row.uploaded_by || '-'}</td>
                      <td className="p-3">
                        {narration.hasNarration ? (
                          <div className="flex flex-wrap items-center gap-1 max-w-[250px]">
                            <Badge variant="secondary" className="bg-purple-100 text-purple-700 text-xs">
                              {narration.currency}
                            </Badge>
                            {narration.narrationParts && narration.narrationParts.length > 0 ? (
                              <span className="text-xs text-slate-600">{narration.narrationParts.join(', ')}</span>
                            ) : (
                              <span className="text-xs text-slate-600">→ {formatCurrency(narration.inrAmount)}</span>
                            )}
                            {narration.hasCrocin && narration.narrationParts?.length > 0 && (
                              <Badge variant="secondary" className="bg-red-100 text-red-700 text-xs">ESR@</Badge>
                            )}
                          </div>
                        ) : (
                          <span className="text-slate-300">-</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          
          {/* Footer with Pagination */}
          <div className="border-t bg-slate-50 px-4 py-3 flex items-center justify-between">
            <div className="text-sm text-slate-600">
              Showing <strong>{((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, total)}</strong> of <strong>{total.toLocaleString()}</strong> records
            </div>
            
            {/* Pagination Controls */}
            <div className="flex items-center gap-2 bg-white p-2 rounded-lg border shadow-sm">
              <Button 
                variant="outline" 
                size="sm"
                disabled={currentPage === 1 || pageLoading}
                onClick={() => handlePageChange(1)}
                title="First Page"
                className="px-2"
              >
                <ChevronLeft className="w-4 h-4" />
                <ChevronLeft className="w-4 h-4 -ml-2" />
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                disabled={currentPage === 1 || pageLoading}
                onClick={() => handlePageChange(currentPage - 1)}
                className="gap-1"
              >
                <ChevronLeft className="w-4 h-4" />
                Prev
              </Button>
              <span className="px-4 py-1.5 text-sm font-medium bg-slate-100 border rounded-md">
                Page {currentPage} of {totalPages}
              </span>
              <Button 
                variant="default" 
                size="sm"
                disabled={currentPage >= totalPages || pageLoading}
                onClick={() => handlePageChange(currentPage + 1)}
                className="gap-1 bg-indigo-600 hover:bg-indigo-700"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                disabled={currentPage >= totalPages || pageLoading}
                onClick={() => handlePageChange(totalPages)}
                title="Last Page"
                className="px-2"
              >
                <ChevronRight className="w-4 h-4" />
                <ChevronRight className="w-4 h-4 -ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesRawDataViewPage;
